import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  DollarSign,
  Clock,
  X,
  ChevronDown,
  GripVertical,
  BarChart3,
  List,
  Columns3,
  ArrowUp,
  ArrowDown,
  Calendar,
  MessageSquare,
  Tag,
  ExternalLink,
  AlertCircle,
  Filter,
  Download,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import api from "../api/client";

const STAGES = [
  { key: "new", label: "New", color: "#3b82f6", bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  { key: "contacted", label: "Contacted", color: "#f59e0b", bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
  { key: "estimate_sent", label: "Estimate Sent", color: "#8b5cf6", bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30" },
  { key: "follow_up", label: "Follow Up", color: "#f97316", bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  { key: "negotiation", label: "Negotiation", color: "#B87333", bg: "bg-copper/10", text: "text-copper", border: "border-copper/30" },
  { key: "won", label: "Won", color: "#22c55e", bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30" },
  { key: "lost", label: "Lost", color: "#6b7280", bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30" },
];

const SOURCE_COLORS = {
  organic: { bg: "bg-green-900/30", text: "text-green-400", label: "Organic" },
  paid: { bg: "bg-blue-900/30", text: "text-blue-400", label: "Paid" },
  direct: { bg: "bg-gray-700/50", text: "text-gray-400", label: "Direct" },
  referral: { bg: "bg-purple-900/30", text: "text-purple-400", label: "Referral" },
  social: { bg: "bg-pink-900/30", text: "text-pink-400", label: "Social" },
  email: { bg: "bg-cyan-900/30", text: "text-cyan-400", label: "Email" },
  gbp: { bg: "bg-amber-900/30", text: "text-amber-400", label: "GBP" },
  manual: { bg: "bg-gray-700/50", text: "text-gray-400", label: "Manual" },
};

const CHART_COLORS = ["#B87333", "#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6", "#f97316", "#ec4899"];

function SourceBadge({ source }) {
  const s = SOURCE_COLORS[source] || SOURCE_COLORS.direct;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function StageBadge({ stage }) {
  const s = STAGES.find((st) => st.key === stage) || STAGES[0];
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function QualityRing({ score, size = 32 }) {
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? "#22c55e" : score >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#1a1a2e" strokeWidth={3} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={3} fill="none" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.3} fontFamily="monospace" transform={`rotate(90 ${size / 2} ${size / 2})`}>
        {score}
      </text>
    </svg>
  );
}

function formatValue(v) {
  if (!v) return "$0";
  return "$" + Number(v).toLocaleString();
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}


// ─── KANBAN VIEW ───

function KanbanView({ siteId, onSelectLead, onAddLead }) {
  const [kanban, setKanban] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragLead, setDragLead] = useState(null);

  const fetchKanban = useCallback(() => {
    api.get(`/websites/${siteId}/leads/kanban/`)
      .then((r) => setKanban(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => { fetchKanban(); }, [fetchKanban]);

  const handleDrop = (stage) => {
    if (!dragLead || dragLead.stage === stage) { setDragLead(null); return; }
    api.post(`/websites/${siteId}/leads/kanban/move/`, { lead_id: dragLead.id, stage })
      .then(() => fetchKanban())
      .catch(() => {});
    setDragLead(null);
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => (
          <div key={s.key} className="min-w-[260px] h-96 bg-dark-300 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {STAGES.map((stg) => {
        const col = kanban[stg.key] || { count: 0, total_value: 0, leads: [] };
        return (
          <div
            key={stg.key}
            className="min-w-[260px] max-w-[280px] flex-shrink-0"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(stg.key)}
          >
            {/* Column header */}
            <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl border-t-2 bg-dark-300`} style={{ borderTopColor: stg.color }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-200">{stg.label}</span>
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono bg-dark-50 text-gray-400">{col.count}</span>
              </div>
              <span className="text-[10px] font-mono text-gray-500">{formatValue(col.total_value)}</span>
            </div>

            {/* Column body */}
            <div className="space-y-2 p-2 bg-dark-300/50 rounded-b-xl min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto">
              {col.leads.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => setDragLead(lead)}
                  onClick={() => onSelectLead(lead.id)}
                  className="bg-[#1a1a2e] border border-dark-50 rounded-lg p-3 cursor-pointer hover:border-copper/40 transition-colors group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-200 truncate">{lead.name || "Unknown"}</div>
                      {lead.service_requested && (
                        <div className="text-[11px] text-gray-500 truncate mt-0.5">{lead.service_requested}</div>
                      )}
                    </div>
                    <GripVertical size={14} className="text-gray-700 group-hover:text-gray-500 flex-shrink-0 mt-1" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SourceBadge source={lead.source} />
                      {lead.phone && <Phone size={11} className="text-gray-600" />}
                    </div>
                    <div className="flex items-center gap-2">
                      {lead.estimated_value && (
                        <span className="text-[11px] font-mono text-copper">{formatValue(lead.estimated_value)}</span>
                      )}
                      <QualityRing score={lead.quality_score} size={24} />
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-600 mt-1.5">{timeAgo(lead.created_at)}</div>
                </div>
              ))}

              {stg.key === "new" && (
                <button
                  onClick={onAddLead}
                  className="w-full py-2 border border-dashed border-dark-50 rounded-lg text-xs text-gray-500 hover:text-copper hover:border-copper/30 transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={14} /> Add Lead
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── LIST VIEW ───

function ListView({ siteId, onSelectLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (stageFilter) params.stage = stageFilter;
    if (sourceFilter) params.source = sourceFilter;
    api.get(`/websites/${siteId}/leads/`, { params })
      .then((r) => setLeads(r.data.results || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId, search, stageFilter, sourceFilter]);

  const handleExport = () => {
    const headers = ["Name", "Email", "Phone", "Service", "Source", "Stage", "Value", "Created"];
    const rows = leads.map((l) => [
      l.name, l.email, l.phone, l.service_requested, l.source, l.stage,
      l.estimated_value || "", new Date(l.created_at).toLocaleDateString(),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Stages</option>
          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Sources</option>
          {Object.entries(SOURCE_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-copper bg-dark-300 border border-dark-50 rounded-lg transition-colors">
          <Download size={14} /> Export
        </button>
      </div>

      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-dark-50">
                <th className="text-left py-3 px-4 font-medium">Name</th>
                <th className="text-left py-3 px-4 font-medium">Contact</th>
                <th className="text-left py-3 px-4 font-medium">Service</th>
                <th className="text-left py-3 px-4 font-medium">Source</th>
                <th className="text-left py-3 px-4 font-medium">Stage</th>
                <th className="text-right py-3 px-4 font-medium">Value</th>
                <th className="text-right py-3 px-4 font-medium">Score</th>
                <th className="text-right py-3 px-4 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">Loading...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">No leads found</td></tr>
              ) : leads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => onSelectLead(lead.id)}
                  className="border-b border-dark-50/50 hover:bg-dark-50/30 cursor-pointer transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-200">{lead.name || "—"}</div>
                    {lead.company && <div className="text-[11px] text-gray-500">{lead.company}</div>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-0.5">
                      {lead.email && <div className="text-[11px] text-gray-400">{lead.email}</div>}
                      {lead.phone && <div className="text-[11px] text-gray-400">{lead.phone}</div>}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-300 max-w-[150px] truncate">{lead.service_requested || "—"}</td>
                  <td className="py-3 px-4"><SourceBadge source={lead.source} /></td>
                  <td className="py-3 px-4"><StageBadge stage={lead.stage} /></td>
                  <td className="py-3 px-4 text-right font-mono text-copper">{lead.estimated_value ? formatValue(lead.estimated_value) : "—"}</td>
                  <td className="py-3 px-4 text-right"><QualityRing score={lead.quality_score} size={28} /></td>
                  <td className="py-3 px-4 text-right text-gray-500 text-[11px]">{timeAgo(lead.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ─── ANALYTICS VIEW ───

function AnalyticsView({ siteId }) {
  const [analytics, setAnalytics] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/leads/analytics/`),
      api.get(`/websites/${siteId}/leads/analytics/timeline/`),
      api.get(`/websites/${siteId}/leads/analytics/funnel/`),
    ])
      .then(([aRes, tRes, fRes]) => {
        setAnalytics(aRes.data);
        setTimeline(tRes.data);
        setFunnel(fRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-dark-300 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-64 bg-dark-300 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!analytics) return <div className="text-gray-500">No analytics data available</div>;

  const stats = analytics.stats || {};
  const sourceData = (analytics.by_source || []).map((s, i) => ({
    name: SOURCE_COLORS[s.source]?.label || s.source,
    value: s.count,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-900/20 rounded-lg"><Users size={18} className="text-blue-400" /></div>
            <span className="text-sm text-gray-400">Leads This Month</span>
          </div>
          <div className="text-2xl font-mono font-bold">{stats.leads_this_month}</div>
        </div>
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-copper/20 rounded-lg"><DollarSign size={18} className="text-copper" /></div>
            <span className="text-sm text-gray-400">Pipeline Value</span>
          </div>
          <div className="text-2xl font-mono font-bold text-copper">{formatValue(stats.pipeline_value)}</div>
        </div>
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-900/20 rounded-lg"><DollarSign size={18} className="text-green-400" /></div>
            <span className="text-sm text-gray-400">Won Revenue</span>
          </div>
          <div className="text-2xl font-mono font-bold text-green-400">{formatValue(stats.won_value_this_month)}</div>
        </div>
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-900/20 rounded-lg"><BarChart3 size={18} className="text-purple-400" /></div>
            <span className="text-sm text-gray-400">Conversion Rate</span>
          </div>
          <div className="text-2xl font-mono font-bold">{stats.conversion_rate}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leads Over Time */}
        <div className="lg:col-span-2 bg-dark-300 border border-dark-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Leads Over Time</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} tickFormatter={(d) => new Date(d).toLocaleDateString("en", { month: "short", day: "numeric" })} interval={6} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="leads" stroke="#B87333" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Leads by Source */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Leads by Source</h3>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={sourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {sourceData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-600 text-sm">No data</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel */}
        {funnel && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Conversion Funnel (30 days)</h3>
            <div className="space-y-3">
              {[
                { label: "Page Visits", value: funnel.visits, color: "bg-blue-500" },
                { label: "Leads", value: funnel.leads, color: "bg-copper" },
                { label: "Contacted", value: funnel.contacted, color: "bg-amber-500" },
                { label: "Won", value: funnel.won, color: "bg-green-500" },
              ].map((step, i) => {
                const maxVal = funnel.visits || 1;
                const pct = Math.max(5, (step.value / maxVal) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">{step.label}</span>
                      <span className="font-mono text-gray-200">{step.value}</span>
                    </div>
                    <div className="w-full bg-dark-50 rounded-full h-3">
                      <div className={`${step.color} rounded-full h-3 transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Converting Pages */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Top Converting Pages</h3>
          <div className="space-y-2">
            {(analytics.by_page || []).slice(0, 5).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-300 truncate max-w-[250px]">{new URL(p.source_page).pathname}</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-gray-400">{p.count} leads</span>
                  <span className="font-mono text-copper">{formatValue(p.value)}</span>
                </div>
              </div>
            ))}
            {(!analytics.by_page || analytics.by_page.length === 0) && (
              <div className="text-gray-600 text-sm">No page data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Top Keywords */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Top Converting Keywords</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(analytics.by_keyword || []).slice(0, 8).map((kw, i) => (
            <div key={i} className="flex items-center justify-between text-sm bg-dark-50/30 rounded-lg px-3 py-2">
              <span className="text-gray-300 font-mono truncate max-w-[200px]">{kw.source_keyword}</span>
              <div className="flex items-center gap-3">
                <span className="text-gray-500">{kw.count} leads</span>
                <span className="font-mono text-copper">{formatValue(kw.value)}</span>
              </div>
            </div>
          ))}
          {(!analytics.by_keyword || analytics.by_keyword.length === 0) && (
            <div className="text-gray-600 text-sm">No keyword data yet</div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── LEAD DETAIL DRAWER ───

function LeadDetailDrawer({ siteId, leadId, onClose, onUpdate }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/leads/${leadId}/`)
      .then((r) => setLead(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId, leadId]);

  const handleStageChange = (newStage) => {
    api.post(`/websites/${siteId}/leads/${leadId}/stage/`, { stage: newStage })
      .then((r) => { setLead(r.data); onUpdate?.(); })
      .catch(() => {});
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    api.post(`/websites/${siteId}/leads/${leadId}/note/`, { note: noteText })
      .then(() => {
        setNoteText("");
        api.get(`/websites/${siteId}/leads/${leadId}/`).then((r) => setLead(r.data));
      })
      .catch(() => {});
  };

  const handleSetFollowUp = () => {
    if (!followUpDate) return;
    api.post(`/websites/${siteId}/leads/${leadId}/follow-up/`, { date: followUpDate })
      .then(() => {
        setShowFollowUp(false);
        setFollowUpDate("");
        api.get(`/websites/${siteId}/leads/${leadId}/`).then((r) => { setLead(r.data); onUpdate?.(); });
      })
      .catch(() => {});
  };

  const handleMarkSpam = () => {
    api.patch(`/websites/${siteId}/leads/${leadId}/`, { is_spam: true })
      .then(() => { onClose(); onUpdate?.(); })
      .catch(() => {});
  };

  if (!leadId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-dark-200 border-l border-dark-50 overflow-y-auto">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-8 bg-dark-300 rounded animate-pulse" />
            <div className="h-40 bg-dark-300 rounded animate-pulse" />
          </div>
        ) : lead ? (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">{lead.name || "Unknown Lead"}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <StageBadge stage={lead.stage} />
                  <SourceBadge source={lead.source} />
                  <QualityRing score={lead.quality_score} size={28} />
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-dark-50 rounded-lg transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Contact Info */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact</h3>
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-gray-300 hover:text-copper transition-colors">
                  <Phone size={14} className="text-gray-500" /> {lead.phone}
                </a>
              )}
              {lead.email && (
                <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-gray-300 hover:text-copper transition-colors">
                  <Mail size={14} className="text-gray-500" /> {lead.email}
                </a>
              )}
              {lead.company && (
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Tag size={14} className="text-gray-500" /> {lead.company}
                </div>
              )}
              {lead.service_requested && (
                <div className="text-sm text-gray-400 mt-2">
                  <span className="text-gray-500">Service: </span>{lead.service_requested}
                </div>
              )}
              {lead.message && (
                <div className="text-sm text-gray-400 mt-2 bg-dark-50/30 rounded-lg p-3">
                  "{lead.message}"
                </div>
              )}
            </div>

            {/* Value */}
            <div className="flex gap-4">
              <div className="flex-1 bg-dark-300 border border-dark-50 rounded-xl p-4">
                <div className="text-xs text-gray-500 mb-1">Estimated Value</div>
                <div className="text-lg font-mono font-bold text-copper">{formatValue(lead.estimated_value)}</div>
              </div>
              {lead.actual_value && (
                <div className="flex-1 bg-dark-300 border border-dark-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">Actual Value</div>
                  <div className="text-lg font-mono font-bold text-green-400">{formatValue(lead.actual_value)}</div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</h3>

              {/* Stage change */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Change Stage</label>
                <select
                  value={lead.stage}
                  onChange={(e) => handleStageChange(e.target.value)}
                  className="w-full bg-dark-50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-copper/50"
                >
                  {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>

              {/* Follow-up */}
              <div>
                {lead.next_follow_up && (
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <Clock size={12} className={new Date(lead.next_follow_up) < new Date() ? "text-red-400" : "text-gray-500"} />
                    <span className={new Date(lead.next_follow_up) < new Date() ? "text-red-400" : "text-gray-400"}>
                      Follow-up: {new Date(lead.next_follow_up).toLocaleDateString()}
                      {new Date(lead.next_follow_up) < new Date() && " (overdue)"}
                    </span>
                  </div>
                )}
                {showFollowUp ? (
                  <div className="flex gap-2">
                    <input
                      type="datetime-local"
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                      className="flex-1 bg-dark-50 border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none"
                    />
                    <button onClick={handleSetFollowUp} className="px-3 py-1.5 bg-copper/20 text-copper rounded-lg text-sm hover:bg-copper/30">Set</button>
                    <button onClick={() => setShowFollowUp(false)} className="px-3 py-1.5 text-gray-500 text-sm">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowFollowUp(true)} className="text-sm text-copper hover:underline flex items-center gap-1">
                    <Calendar size={12} /> Set Follow-up
                  </button>
                )}
              </div>

              <button onClick={handleMarkSpam} className="text-xs text-red-400 hover:underline">Mark as Spam</button>
            </div>

            {/* Attribution */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Attribution</h3>
              {lead.source_page && (
                <div className="text-sm">
                  <span className="text-gray-500">Page: </span>
                  <a href={lead.source_page} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline inline-flex items-center gap-1">
                    {new URL(lead.source_page).pathname} <ExternalLink size={10} />
                  </a>
                </div>
              )}
              {lead.source_keyword && (
                <div className="text-sm"><span className="text-gray-500">Keyword: </span><span className="text-gray-300 font-mono">{lead.source_keyword}</span></div>
              )}
              {lead.referrer_url && (
                <div className="text-sm"><span className="text-gray-500">Referrer: </span><span className="text-gray-300">{lead.referrer_url}</span></div>
              )}
              {(lead.utm_source || lead.utm_medium || lead.utm_campaign) && (
                <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                  {lead.utm_source && <div>utm_source: {lead.utm_source}</div>}
                  {lead.utm_medium && <div>utm_medium: {lead.utm_medium}</div>}
                  {lead.utm_campaign && <div>utm_campaign: {lead.utm_campaign}</div>}
                </div>
              )}
            </div>

            {/* Add Note */}
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Add Note</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Write a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                  className="flex-1 bg-dark-50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50"
                />
                <button onClick={handleAddNote} className="px-3 py-2 bg-copper/20 text-copper rounded-lg text-sm hover:bg-copper/30">Add</button>
              </div>
            </div>

            {/* Activity Timeline */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Activity</h3>
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-2 top-0 bottom-0 w-px bg-dark-50" />
                {(lead.activities || []).map((a) => {
                  const iconMap = {
                    note: MessageSquare, stage_change: ChevronDown, auto_capture: Plus,
                    call_made: Phone, call_received: Phone, email_sent: Mail,
                    follow_up_set: Calendar, assigned: Users,
                  };
                  const Icon = iconMap[a.activity_type] || MessageSquare;
                  return (
                    <div key={a.id} className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-dark-300 border-2 border-dark-50 flex items-center justify-center">
                        <Icon size={8} className="text-gray-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-300">{a.description}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">
                          {a.created_by_name} · {timeAgo(a.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-gray-500">Lead not found</div>
        )}
      </div>
    </div>
  );
}


// ─── ADD LEAD MODAL ───

function AddLeadModal({ siteId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service_requested: "",
    source: "manual", estimated_value: "", stage: "new",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...form };
    if (data.estimated_value) data.estimated_value = parseFloat(data.estimated_value);
    else delete data.estimated_value;

    api.post(`/websites/${siteId}/leads/`, data)
      .then(() => { onCreated(); onClose(); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-dark-200 border border-dark-50 rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">Add Lead</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50 rounded-lg"><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50" />
          </div>
          <input placeholder="Service requested" value={form.service_requested} onChange={(e) => setForm({ ...form, service_requested: e.target.value })} className="w-full bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 focus:outline-none">
              {Object.entries(SOURCE_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input placeholder="Estimated value ($)" type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-copper/50" />
          </div>
          <button type="submit" disabled={saving} className="w-full py-2.5 bg-copper hover:bg-copper/90 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {saving ? "Adding..." : "Add Lead"}
          </button>
        </form>
      </div>
    </div>
  );
}


// ─── MAIN COMPONENT ───

export default function LeadsCRM({ selectedSite }) {
  const [tab, setTab] = useState("kanban");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  if (!selectedSite) {
    return <div className="text-gray-500">Select a website to view leads</div>;
  }

  const siteId = selectedSite.id;
  const refresh = () => setRefreshKey((k) => k + 1);

  const tabs = [
    { key: "kanban", label: "Kanban", icon: Columns3 },
    { key: "list", label: "List", icon: List },
    { key: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Leads</h2>
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  tab === t.key ? "bg-copper/10 text-copper" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-copper hover:bg-copper/90 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> Add Lead
        </button>
      </div>

      {/* Tab content */}
      {tab === "kanban" && (
        <KanbanView key={refreshKey} siteId={siteId} onSelectLead={setSelectedLeadId} onAddLead={() => setShowAddModal(true)} />
      )}
      {tab === "list" && (
        <ListView key={refreshKey} siteId={siteId} onSelectLead={setSelectedLeadId} />
      )}
      {tab === "analytics" && (
        <AnalyticsView key={refreshKey} siteId={siteId} />
      )}

      {/* Detail drawer */}
      {selectedLeadId && (
        <LeadDetailDrawer
          siteId={siteId}
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          onUpdate={refresh}
        />
      )}

      {/* Add modal */}
      {showAddModal && (
        <AddLeadModal
          siteId={siteId}
          onClose={() => setShowAddModal(false)}
          onCreated={refresh}
        />
      )}
    </div>
  );
}
